import { useCallback, useEffect, useState } from 'react';
import { useApi, useMe } from '../context';
import { getPref, getPrefList, setPref, setPrefList } from '../realtime';

const AUTO_OPEN_KEY = 'hlh.autoOpenRingingCall';
const AUTO_OPEN_LINES_KEY = 'hlh.autoOpenRingingLines';

interface Member { id: string; name: string }
interface Stage {
  position: number;
  kind: 'shift_group' | 'users' | 'phone' | 'voicemail' | 'ivr' | 'time';
  timeoutSeconds: number;
  teamName?: string | null;
  groupName?: string | null;
  members?: Member[];
  number?: string | null;
  /** ivr only */
  inputTimeoutSeconds?: number;
  options?: { digit: string; label: string; stages: Stage[] }[];
  noInput?: Stage[] | null;
  /** time only */
  timeZone?: string;
  entries?: { label: string; days: number[]; start: string; end: string; activeNow: boolean; stages: Stage[] }[];
  otherwise?: Stage[] | null;
  otherwiseActiveNow?: boolean;
}
const DAY_LABEL: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const daysText = (days: number[]) => days.map((d) => DAY_LABEL[d] ?? d).join(' ');
interface Line {
  id: string;
  name: string;
  number: string | null;
  isActive: boolean;
  stages: Stage[];
}

const KIND_LABEL: Record<Stage['kind'], string> = {
  shift_group: 'On shift now', users: 'Specific people', phone: 'External number', voicemail: 'Voicemail', ivr: 'Menu', time: 'Time condition',
};

/** `first`: this stage is where a new call starts, so its active branch holds the first recipients. */
function StageHandlers({ stage, first }: { stage: Stage; first?: boolean }) {
  if (stage.kind === 'voicemail') return <span className="sub">Records the caller a message.</span>;
  if (stage.kind === 'time') {
    return (
      <div className="ivr-branches">
        {(stage.entries ?? []).map((e, i) => (
          <div key={i} className={`ivr-branch${e.activeNow ? ' active-now' : ''}`}>
            <span className={`pill${e.activeNow ? ' ok' : ''}`}>{e.label || `Entry ${i + 1}`}</span>
            <span className="sub"> {daysText(e.days)} {e.start}–{e.end}{e.start === e.end ? ' (all day)' : e.start > e.end ? ' (overnight)' : ''}</span>
            {e.activeNow && <span className="badge">active now</span>}
            <StageTree stages={e.stages} first={first && e.activeNow} />
          </div>
        ))}
        <div className={`ivr-branch${stage.otherwiseActiveNow ? ' active-now' : ''}`}>
          <span className={`pill${stage.otherwiseActiveNow ? ' ok' : ''}`}>Otherwise</span>
          {stage.otherwiseActiveNow && <span className="badge">active now</span>}
          {stage.otherwise ? <StageTree stages={stage.otherwise} first={first && stage.otherwiseActiveNow} /> : <span className="sub"> continues with the stages after the condition</span>}
        </div>
      </div>
    );
  }
  if (stage.kind === 'ivr') {
    return (
      <div className="ivr-branches">
        {(stage.options ?? []).map((o) => (
          <div key={o.digit} className="ivr-branch">
            <span className="pill">Press {o.digit}</span> <span className="sub">{o.label}</span>
            <StageTree stages={o.stages} />
          </div>
        ))}
        <div className="ivr-branch">
          <span className="pill">No key</span>
          {stage.noInput ? <StageTree stages={stage.noInput} /> : <span className="sub"> continues with the stages after the menu</span>}
        </div>
      </div>
    );
  }
  if (stage.kind === 'phone') return <span>{stage.number ?? '—'}</span>;
  const members = stage.members ?? [];
  if (members.length === 0) {
    return <span className="sub">{stage.kind === 'shift_group' ? 'Nobody on shift right now' : 'No one configured'}</span>;
  }
  return (
    <span className="handlers">
      {members.map((m) => <span key={m.id} className="pill">{m.name}</span>)}
    </span>
  );
}

/** Stages (a plan or a menu branch) with each resolved to who handles it now. */
function StageTree({ stages, first }: { stages: Stage[]; first?: boolean }) {
  if (stages.length === 0) return <span className="sub"> nothing configured</span>;
  return (
    <ol className="stage-list">
      {stages.map((st, i) => (
        <li key={st.position} className={first && i === 0 ? 'first-stage' : ''}>
          <div className="stage-head">
            <span className="stage-kind">{KIND_LABEL[st.kind]}</span>
            {st.kind === 'shift_group' && st.groupName && <span className="sub"> · {st.groupName}</span>}
            {first && i === 0 && st.kind !== 'time' && st.kind !== 'ivr' && <span className="badge first">first recipients</span>}
            {st.kind === 'ivr' && <span className="sub"> · waits {st.inputTimeoutSeconds ?? 5}s for a key</span>}
            {st.kind === 'time' && <span className="sub"> · {st.timeZone ?? 'Europe/London'}</span>}
            {st.kind !== 'voicemail' && st.kind !== 'ivr' && st.kind !== 'time' && <span className="sub"> · rings {st.timeoutSeconds}s</span>}
          </div>
          <div className="stage-handlers"><StageHandlers stage={st} first={first && i === 0} /></div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Operational overview for people who take calls: each line, its current first-recipient
 * group, and every escalation stage resolved to who would handle it right now (on-shift
 * members are live from Teams Shifts). Polls every 30 s so rota changes surface. Also hosts
 * the per-viewer "auto-open ringing calls" setting and which lines to monitor.
 */
export function LinesPage() {
  const api = useApi();
  const me = useMe();
  const [lines, setLines] = useState<Line[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoOpen, setAutoOpen] = useState(() => getPref(AUTO_OPEN_KEY));
  const [monitored, setMonitored] = useState<string[]>(() => getPrefList(AUTO_OPEN_LINES_KEY));
  const canHandle = me.roles.some((r) => r === 'on_call_user' || r === 'manager' || r === 'admin');

  const load = useCallback(() => {
    api.get<Line[]>('/api/lines').then(setLines, (e: Error) => setError(e.message));
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const setAutoOpenPref = (on: boolean) => { setAutoOpen(on); setPref(AUTO_OPEN_KEY, on); };
  const toggleMonitor = (number: string) => {
    const adding = !monitored.includes(number);
    const next = adding ? [...monitored, number] : monitored.filter((n) => n !== number);
    setMonitored(next);
    setPrefList(AUTO_OPEN_LINES_KEY, next);
    // Ticking a line means "open this line's calls": make sure the master switch is on too,
    // otherwise the tick silently does nothing.
    if (adding && !autoOpen) setAutoOpenPref(true);
  };

  return (
    <>
      <h1>Lines</h1>
      {error && <p className="error">{error}</p>}

      {canHandle && (
        <div className="line-card">
          <label>
            <input
              type="checkbox"
              checked={autoOpen}
              onChange={(e) => setAutoOpenPref(e.target.checked)}
            />{' '}
            <strong>Auto-open ringing calls</strong>
          </label>
          <p className="hint">
            When on, a ringing call opens automatically. Tick the lines below to limit this to
            specific lines — with none ticked, every line auto-opens. This is a setting of this
            browser, and a call never opens over a call page you already have open.
            {!autoOpen && monitored.length > 0 && <strong> Auto-open is off, so the ticked lines will not open.</strong>}
          </p>
        </div>
      )}

      {lines.length === 0 && !error && <p className="sub">No lines configured.</p>}

      {lines.map((line) => (
        <div key={line.id} className={`line-card${line.isActive ? '' : ' inactive'}`}>
          <div className="filters">
            <strong>{line.name}</strong>
            <span className="sub">{line.number ?? 'no number'}</span>
            <span className={`pill ${line.isActive ? 'ok' : ''}`}>{line.isActive ? 'active' : 'disabled'}</span>
            <span className="spacer" />
            {canHandle && line.number && (
              <label className="sub">
                <input type="checkbox" checked={monitored.includes(line.number)} onChange={() => toggleMonitor(line.number!)} />
                {' '}Monitor (auto-open)
              </label>
            )}
          </div>

          {line.stages.length === 0 ? (
            <p className="sub">No call flow configured.</p>
          ) : (
            <StageTree stages={line.stages} first />
          )}
        </div>
      ))}
    </>
  );
}
